import {
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@material-ui/core';
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import NumberFormat from 'react-number-format';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { useState } from 'react';
import { noDataAvailable } from '../../../components/NoDataAvailable';
import { PartnerId } from '../../../components/Partner/PartnerId';
import IProduct from '../../../models/Product.model';
import { IVenue } from '../../../models/Venue.model';
import {
  getCurrencyPrefix,
  getProductImage,
} from '../../../services/Util/Util.service';
import { VALID_CATEGORY_TYPES } from '../Categories/Category.config';

const menuTableColumnNames = [
  { name: 'Photo', size: 'small' },
  { name: 'Name', size: 'medium' },
  { name: 'Price', size: 'small' },
  { name: 'Active', size: 'small' },
  // { name: 'Stock', size: 'small' }, We don't use stock at all right now
  { name: 'Priority', size: 'small' },
  { name: 'Inventory', size: 'small' },
  { name: 'Availability Type', size: 'small' },
  { name: 'Action', size: 'medium' },
];

const optionsTableColumnNames = [
  { name: '', size: 'small' },
  { name: 'Name', size: 'medium' },
  { name: 'Priority', size: 'small' },
  { name: 'Inventory', size: 'small' },
  { name: 'Action', size: 'medium' },
];

interface IMenuTableProps {
  isOptionsCategory: boolean;
  onDeleteProduct: (productId: string) => void;
  onSelectProduct: (productId: string) => void;
  products: IProduct[];
  toggleProductActive: (productId: string, active: boolean) => Promise<void>;
  venue: IVenue;
  onReorderProducts?: (updated: IProduct[]) => void;
}

const MenuTable = ({
  isOptionsCategory,
  onDeleteProduct,
  onSelectProduct,
  products,
  toggleProductActive,
  venue,
  onReorderProducts,
}: IMenuTableProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [draggableProducts, setDraggableProducts] = useState<IProduct[]>(
    products,
  );

  useEffect(() => {
    setDraggableProducts(products);
  }, [products]);

  const columnNames = isOptionsCategory
    ? optionsTableColumnNames
    : menuTableColumnNames;

  const handleActiveToggle = (product: IProduct, active: boolean) => {
    const isPriceZero = product.price === 0;
    const isOptionType =
      product.category &&
      product.category.type === VALID_CATEGORY_TYPES.OPTIONS;

    if (!venue.allowFreeProducts && isPriceZero && !isOptionType && active) {
      enqueueSnackbar!('Price must be greater than zero prior to activating.', {
        variant: 'error',
        autoHideDuration: 8000,
      });
      return;
    }

    toggleProductActive(product.id, active);
  };
  useEffect(() => {
    draggableProducts.map((productData: any) => {
      const availability = [];
      if (productData?.availabilityType === 'ALL') {
        availability.push('PICKUP', 'DELIVERY');
      } else if (productData?.availabilityType !== 'NONE') {
        availability.push(productData?.availabilityType);
      }
      if (productData.pos) {
        availability.push('POS');
      }
      productData.testAvailabilityType = availability;
    });
  }, [draggableProducts]);

  // Re-ordering util
  const reorder = (list: IProduct[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    // Update priorities after reorder
    result.forEach((p, idx) => {
      p.priority = idx + 1;
    });
    return result;
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      draggableProducts,
      result.source.index,
      result.destination.index,
    );

    setDraggableProducts(newItems);

    // Notify parent so it can persist priorities if a callback was provided.
    if (onReorderProducts) {
      onReorderProducts(newItems);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Table className="menu__table">
        <Droppable droppableId="products-droppable" direction="vertical">
          {providedDroppable => (
            <TableBody
              ref={providedDroppable.innerRef}
              {...providedDroppable.droppableProps}
            >
              <TableRow classes={{ root: 'menu__table-header' }}>
                {columnNames.map((column: any, i: number) => (
                  <TableCell
                    key={i}
                    align="left"
                    size={column.size}
                    variant="head"
                    classes={{ root: 'menu__table-cell' }}
                  >
                    <p>{column.name}</p>
                  </TableCell>
                ))}
              </TableRow>
              {draggableProducts.length < 1
                ? noDataAvailable(columnNames.length)
                : draggableProducts.map((product: IProduct, index: number) => (
                    <Draggable
                      key={product.id}
                      draggableId={product.id}
                      index={index}
                    >
                      {providedDraggable => (
                        <TableRow
                          ref={providedDraggable.innerRef}
                          hover={true}
                          {...providedDraggable.draggableProps}
                          {...providedDraggable.dragHandleProps}
                        >
                          <TableCell
                            key={`product ${product.id}_photo`}
                            align="center"
                            size="small"
                            variant="body"
                            classes={{ root: 'menu__table-cell photo' }}
                          >
                            {getProductImage(product) && (
                              <div className="menu__product-wrapper">
                                <img
                                  alt="product"
                                  className="menu__product-image"
                                  src={getProductImage(product)}
                                />
                              </div>
                            )}
                            <DragIndicatorIcon
                              fontSize="small"
                              className="drag-handle-icon"
                              style={{
                                marginRight: 4,
                                verticalAlign: 'middle',
                              }}
                            />
                          </TableCell>
                          <TableCell
                            key={`product ${product.id}_name`}
                            align="left"
                            size="medium"
                            variant="body"
                            classes={{ root: 'menu__table-cell' }}
                          >
                            <p>{product.name}</p>
                            <PartnerId
                              id={product.partnerProductId}
                              partner={product.partner}
                            />
                          </TableCell>
                          {!isOptionsCategory && (
                            <TableCell
                              key={`product ${product.id}_price`}
                              align="left"
                              size="small"
                              variant="body"
                              classes={{ root: 'menu__table-cell' }}
                            >
                              <p>
                                <NumberFormat
                                  value={product.price}
                                  decimalScale={2}
                                  displayType={'text'}
                                  fixedDecimalScale={true}
                                  thousandSeparator={true}
                                  prefix={getCurrencyPrefix(venue.currency)}
                                />
                              </p>
                            </TableCell>
                          )}

                          {!isOptionsCategory && (
                            <TableCell
                              key={`product ${product.id}_active`}
                              align="left"
                              size="small"
                              variant="body"
                              classes={{ root: 'menu__table-cell' }}
                            >
                              <Switch
                                classes={{
                                  track: 'custom-colored-switch-track',
                                }}
                                checked={product.active}
                                color="primary"
                                name="active"
                                disabled={
                                  product.stock < 1 &&
                                  product.countAsInventory === true
                                    ? true
                                    : false
                                }
                                onChange={(e, checked) =>
                                  handleActiveToggle(product, checked)
                                }
                                size="medium"
                                value={product.active}
                              />
                            </TableCell>
                          )}
                          {/* We don't use stock at all right now, when we re-implement the inventory feature, we can re-enable this
                {!isOptionsCategory && (
                  <TableCell
                    key={`product ${product.id}_stock`}
                    align="left"
                    size="small"
                    variant="body"
                    classes={{ root: 'menu__table-cell' }}
                  >
                    <p>{product.stock}</p>
                  </TableCell>
                )} */}
                          <TableCell
                            key={`product ${product.id}_priority`}
                            align="left"
                            size="small"
                            variant="body"
                            classes={{ root: 'menu__table-cell' }}
                          >
                            <p>{product.priority}</p>
                          </TableCell>
                          <TableCell
                            key={`product ${product.id}_stock`}
                            align="left"
                            size="small"
                            variant="body"
                            classes={{ root: 'menu__table-cell' }}
                          >
                            <p>{product.stock}</p>
                          </TableCell>
                          {!isOptionsCategory && product?.testAvailabilityType && (
                            <TableCell
                              key={`product ${product.id}_availability-type`}
                              align="left"
                              size="small"
                              variant="body"
                              classes={{ root: 'menu__table-cell' }}
                            >
                              <p>{product.testAvailabilityType.join(', ')}</p>
                            </TableCell>
                          )}
                          <TableCell
                            key={`product ${product.id}_action`}
                            align="left"
                            size="medium"
                            variant="body"
                            classes={{ root: 'menu__table-cell' }}
                          >
                            <Button
                              size="small"
                              classes={{ root: 'menu__action-buttons' }}
                              onClick={() => onSelectProduct(product.id)}
                            >
                              <div className="icon-title-wrapper">
                                <CreateOutlinedIcon />
                                <span className="icon-title">Edit</span>
                              </div>
                            </Button>
                            <Button
                              size="small"
                              classes={{ root: 'menu__action-buttons' }}
                              onClick={() => onDeleteProduct(product.id)}
                            >
                              <div className="icon-title-wrapper">
                                <DeleteOutlinedIcon />
                                <span className="icon-title">Delete</span>
                              </div>
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
              {providedDroppable.placeholder}
            </TableBody>
          )}
        </Droppable>
      </Table>
    </DragDropContext>
  );
};

export default MenuTable;
